/**
 * How It Works Page Component
 * Detailed explanation of the CODEMAKER TaaS process
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import './HowItWorks.css';

// Icons
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="5" y1="12" x2="19" y2="12" />
        <polyline points="12 5 19 12 12 19" />
    </svg>
);

// Timeline steps data
const timelineSteps = [
    {
        step: 1,
        title: 'Discovery & Requirements',
        description: 'We begin with an in-depth consultation to understand your project needs, team requirements, and business goals.',
        details: [
            'Define project scope and objectives',
            'Determine developer seniority level needed',
            'Assess technical stack requirements',
            'Agree on engagement tenure (3-12+ months)',
        ],
    },
    {
        step: 2,
        title: 'Developer Matching & Vetting',
        description: 'Our team carefully matches and vets developers based on your specific requirements and project needs.',
        details: [
            'Review candidate profiles and portfolios',
            'Conduct technical skill assessments',
            'Evaluate communication and soft skills',
            'Present top candidates for your approval',
        ],
    },
    {
        step: 3,
        title: 'Onboarding & Setup',
        description: 'We handle the complete onboarding process to ensure a smooth start to your engagement.',
        details: [
            'Introduction and team integration',
            'Development environment setup',
            'Access provisioning and security',
            'Project kickoff and milestone planning',
        ],
    },
    {
        step: 4,
        title: 'Day-to-Day Collaboration',
        description: 'Your developer works directly with you on daily tasks while we provide background support.',
        details: [
            'Direct communication via your preferred tools',
            'Daily standups or check-ins as needed',
            'Agile workflow integration',
            'Continuous delivery and feedback loops',
        ],
    },
    {
        step: 5,
        title: 'Tech Lead Oversight & Quality',
        description: 'Our technical leadership ensures code quality and alignment with best practices throughout.',
        details: [
            'Weekly code reviews and feedback',
            'Architecture guidance and decisions',
            'Technical debt management',
            'Performance optimization reviews',
        ],
    },
    {
        step: 6,
        title: 'Reporting & Communication',
        description: 'Stay informed with regular progress updates and transparent communication.',
        details: [
            'Bi-weekly written progress reports',
            'Monthly stakeholder reviews (optional)',
            'Milestone tracking and updates',
            'Issue escalation and resolution',
        ],
    },
];

// Model explanation cards
const modelCards = [
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
        ),
        title: 'Developer-Only Delivery',
        description: 'You get dedicated developers who work exclusively on your projects. No shared resources, no divided attention. Your developer integrates with your team as if they were in-house.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
        ),
        title: 'Tech Lead Management',
        description: 'Our Tech Lead provides strategic oversight without micromanaging. This means guidance on architecture, code quality checks, and ensuring your project stays on track technically.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
        ),
        title: 'Direct Communication',
        description: 'No middlemen or project managers in the way. You communicate directly with your developer(s) daily via Slack, Teams, or any tool you prefer. Fast decisions, no delays.',
    },
    {
        icon: (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
        ),
        title: 'Subscription-Based',
        description: 'Simple monthly billing with no surprise costs. Choose your tenure (3-12+ months) and lock in predictable rates. The longer you commit, the more you save.',
    },
];

// CTO oversight items
const ctoItems = [
    'Weekly technical review sessions',
    'Code quality and security audits',
    'Architecture decision support',
    'Best practices enforcement',
    'Technical debt management',
    'Performance optimization guidance',
];

function HowItWorks() {
    return (
        <>
            <Helmet>
                <title>How It Works | CODEMAKER Technology TaaS Process</title>
                <meta
                    name="description"
                    content="Learn how CODEMAKER's Talent-as-a-Service works. From discovery to delivery, understand our developer matching, onboarding, and Tech Lead oversight process."
                />
            </Helmet>

            <main>
                {/* Hero Section */}
                <section className="hiw-hero">
                    <div className="hiw-hero__container">
                        <span className="hiw-hero__badge">Our Process</span>
                        <h1 className="hiw-hero__title">How It Works</h1>
                        <p className="hiw-hero__description">
                            From initial consultation to ongoing delivery, here's how we match you
                            with the right developers and ensure project success.
                        </p>
                    </div>
                </section>

                {/* Timeline Section */}
                <Section>
                    <Section.Header
                        subtitle="Step by Step"
                        title="Your Journey to Success"
                        description="A transparent, structured process designed to get you building faster."
                    />

                    <div className="timeline">
                        {timelineSteps.map((item, index) => (
                            <div key={index} className="timeline__item">
                                <div className="timeline__marker">{item.step}</div>
                                <div className="timeline__content">
                                    <span className="timeline__step-label">Step {item.step}</span>
                                    <h3 className="timeline__title">{item.title}</h3>
                                    <p className="timeline__description">{item.description}</p>
                                    <div className="timeline__details">
                                        {item.details.map((detail, idx) => (
                                            <div key={idx} className="timeline__detail">
                                                <CheckIcon />
                                                <span>{detail}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* Our Model Section */}
                <Section background="light">
                    <Section.Header
                        subtitle="Our Model"
                        title="The CODEMAKER Difference"
                        description="A unique approach that combines dedicated talent with expert oversight."
                    />

                    <div className="model-grid">
                        {modelCards.map((card, index) => (
                            <div key={index} className="model-card">
                                <div className="model-card__icon">{card.icon}</div>
                                <h3 className="model-card__title">{card.title}</h3>
                                <p className="model-card__description">{card.description}</p>
                            </div>
                        ))}
                    </div>
                </Section>

                {/* Communication Flow */}
                <Section>
                    <Section.Header
                        subtitle="Clear Communication"
                        title="Simple, Direct Collaboration"
                        description="No bureaucracy. Direct access to your developer with Tech Lead oversight in the background."
                    />

                    <div className="comm-flow">
                        <div className="comm-flow__item">
                            <div className="comm-flow__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
                                </svg>
                            </div>
                            <span className="comm-flow__label">Your Company</span>
                        </div>

                        <div className="comm-flow__arrow">
                            <ArrowRightIcon />
                        </div>

                        <div className="comm-flow__item">
                            <div className="comm-flow__icon" style={{ background: 'linear-gradient(135deg, var(--color-accent-100), var(--color-accent-50))', color: 'var(--color-accent-600)' }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                            </div>
                            <span className="comm-flow__label">Your Developer</span>
                        </div>

                        <div className="comm-flow__arrow">
                            <ArrowRightIcon />
                        </div>

                        <div className="comm-flow__item">
                            <div className="comm-flow__icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                                </svg>
                            </div>
                            <span className="comm-flow__label">Tech Lead Oversight</span>
                        </div>
                    </div>
                </Section>

                {/* CTO Oversight Detail */}
                <Section background="light">
                    <div className="cto-info">
                        <div className="cto-info__content">
                            <h3>What Tech Lead Oversight Means for You</h3>
                            <p>
                                Every engagement includes Tech Lead oversight at no extra cost.
                                This ensures enterprise-grade quality and technical excellence in every project.
                            </p>
                            <div className="cto-info__list">
                                {ctoItems.map((item, index) => (
                                    <div key={index} className="cto-info__item">
                                        <CheckIcon />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="cto-info__visual">
                            <div className="cto-info__icon-large">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                                </svg>
                            </div>
                            <h4>Quality Guaranteed</h4>
                            <p>Technical leadership ensures your project meets industry standards.</p>
                        </div>
                    </div>
                </Section>

                {/* CTA Section */}
                <Section background="gradient" spacing="large">
                    <div className="cta">
                        <h2 className="cta__title">Ready to Get Started?</h2>
                        <p className="cta__description">
                            Let's discuss your project needs and match you with the right developers.
                            Book a free consultation today.
                        </p>
                        <div className="cta__actions">
                            <Button as={Link} to="/contact" variant="primary" size="large">
                                Book a Free Consultation
                            </Button>
                            <Button as={Link} to="/pricing" variant="ghost" size="large">
                                View Pricing
                            </Button>
                        </div>
                    </div>
                </Section>
            </main>
        </>
    );
}

export default HowItWorks;
